import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import type {
  AuthResponse,
  LoginCredentials,
  Client,
  ClientBranch,
  ClientFormData,
  Delivery,
  DeliveryFormData,
  DeliveryStockItem,
  Destination,
  DestinationFormData,
  EsgReport,
  EsgReportFormData,
  EsgReportMetrics,
  OperatorDailyLog,
  OperatorDailyLogFormData,
  OperatorDailyLogSummary,
  Pickup,
  PickupFormData,
  PickupOptions,
  PickupRecord,
  WasteCategory,
  WasteSubcategory,
  WasteType,
  DashboardFilters,
  DashboardSummary,
  KilosByClient,
  KilosBySubcategory,
  KilosByDestination,
  MonthlyTrendPoint,
  OperationalAlerts,
  SystemUser,
  UserFormData,
  User,
  SegregationStatus,
} from '@/types'

const TOKEN_KEY = 'puroviento_token'
const USER_KEY = 'puroviento_user'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

export function setStoredUser(user: User): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

export function removeStoredUser(): void {
  localStorage.removeItem(USER_KEY)
}

// ─── Instancia de Axios ───────────────────────────────────────────────────────
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Interceptor request: añade Bearer token
api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Interceptor response: maneja 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      removeToken()
      removeStoredUser()
      // Redirige a login sin usar el router (para evitar dependencia circular)
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/api/auth/login', credentials),

  logout: (): Promise<AxiosResponse<void>> =>
    api.post('/api/auth/logout'),

  me: (): Promise<AxiosResponse<AuthResponse['user']>> =>
    api.get('/api/auth/me'),
}

// ─── Clientes ─────────────────────────────────────────────────────────────────
export const clientsApi = {
  list: (): Promise<AxiosResponse<Client[]>> =>
    api.get('/api/clients'),

  get: (id: number): Promise<AxiosResponse<Client>> =>
    api.get(`/api/clients/${id}`),

  create: (data: ClientFormData): Promise<AxiosResponse<Client>> =>
    api.post('/api/clients', data),

  update: (id: number, data: ClientFormData): Promise<AxiosResponse<Client>> =>
    api.put(`/api/clients/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/clients/${id}`),

  listBranches: (id: number): Promise<AxiosResponse<ClientBranch[]>> =>
    api.get(`/api/clients/${id}/branches`),

  createBranch: (id: number, data: ClientBranch): Promise<AxiosResponse<ClientBranch>> =>
    api.post(`/api/clients/${id}/branches`, data),

  updateBranch: (id: number, branchId: number, data: ClientBranch): Promise<AxiosResponse<ClientBranch>> =>
    api.put(`/api/clients/${id}/branches/${branchId}`, data),

  deleteBranch: (id: number, branchId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/clients/${id}/branches/${branchId}`),
}

// ─── Jerarquía de residuos ────────────────────────────────────────────────────
export const wasteCategoriesApi = {
  list: (): Promise<AxiosResponse<WasteCategory[]>> =>
    api.get('/api/waste-categories'),

  create: (data: { name: string; is_active?: boolean }): Promise<AxiosResponse<WasteCategory>> =>
    api.post('/api/waste-categories', data),

  update: (id: number, data: { name: string; is_active?: boolean }): Promise<AxiosResponse<WasteCategory>> =>
    api.put(`/api/waste-categories/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/waste-categories/${id}`),
}

export const wasteSubcategoriesApi = {
  list: (): Promise<AxiosResponse<WasteSubcategory[]>> =>
    api.get('/api/waste-subcategories'),

  create: (data: { waste_category_id: number; name: string; is_active?: boolean }): Promise<AxiosResponse<WasteSubcategory>> =>
    api.post('/api/waste-subcategories', data),

  update: (id: number, data: { waste_category_id: number; name: string; is_active?: boolean }): Promise<AxiosResponse<WasteSubcategory>> =>
    api.put(`/api/waste-subcategories/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/waste-subcategories/${id}`),
}

// ─── Tipos de residuo ─────────────────────────────────────────────────────────
export const wasteTypesApi = {
  list: (): Promise<AxiosResponse<WasteType[]>> =>
    api.get('/api/waste-types'),

  get: (id: number): Promise<AxiosResponse<WasteType>> =>
    api.get(`/api/waste-types/${id}`),

  create: (data: { name: string; waste_subcategory_id: number; unit?: string; emission_factor?: number | null }): Promise<AxiosResponse<WasteType>> =>
    api.post('/api/waste-types', data),

  update: (id: number, data: { name: string; waste_subcategory_id: number; unit?: string; emission_factor?: number | null }): Promise<AxiosResponse<WasteType>> =>
    api.put(`/api/waste-types/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/waste-types/${id}`),
}

// ─── Retiros ──────────────────────────────────────────────────────────────────
export const pickupsApi = {
  list: (params?: {
    month?: string
    date_from?: string
    date_to?: string
    client_id?: number
    status?: string
  }): Promise<AxiosResponse<Pickup[]>> =>
    api.get('/api/pickups', { params }),

  get: (id: number): Promise<AxiosResponse<Pickup>> =>
    api.get(`/api/pickups/${id}`),

  create: (data: PickupFormData): Promise<AxiosResponse<Pickup>> =>
    api.post('/api/pickups', data),

  update: (id: number, data: Partial<PickupFormData>): Promise<AxiosResponse<Pickup>> =>
    api.put(`/api/pickups/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/pickups/${id}`),

  getOptions: (): Promise<AxiosResponse<PickupOptions>> =>
    api.get('/api/pickups/options'),
}

// ─── Registros operativos ─────────────────────────────────────────────────────
export const pickupRecordsApi = {
  list: (pickupId?: number): Promise<AxiosResponse<PickupRecord[]>> =>
    api.get('/api/pickup-records', { params: { pickup_id: pickupId } }),

  get: (id: number): Promise<AxiosResponse<PickupRecord>> =>
    api.get(`/api/pickup-records/${id}`),

  create: (data: {
    pickup_id: number
    local_uuid?: string
    started_at: string
    finished_at: string
    notes: string
    items: {
      waste_type_id: number
      kilos: number
      segregation_status: SegregationStatus
      notes: string
    }[]
  }): Promise<AxiosResponse<PickupRecord>> =>
    api.post('/api/pickup-records', data),

  update: (
    id: number,
    data: {
      pickup_id: number
      local_uuid?: string
      started_at: string
      finished_at: string
      notes: string
      items: {
        waste_type_id: number
        kilos: number
        segregation_status: SegregationStatus
        notes: string
      }[]
    }
  ): Promise<AxiosResponse<PickupRecord>> =>
    api.put(`/api/pickup-records/${id}`, data),

  uploadPhoto: (
    recordId: number,
    photo: Blob,
    itemId?: number | null
  ): Promise<AxiosResponse<{ url: string; pickup_record_item_id: number | null }>> => {
    const formData = new FormData()
    formData.append('photo', photo, `photo_${Date.now()}.jpg`)
    if (itemId != null) {
      formData.append('pickup_record_item_id', String(itemId))
    }
    return api.post(`/api/pickup-records/${recordId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// ─── Destinos finales ─────────────────────────────────────────────────────────
export const destinationsApi = {
  list: (): Promise<AxiosResponse<Destination[]>> =>
    api.get('/api/destinations'),

  create: (data: DestinationFormData): Promise<AxiosResponse<Destination>> =>
    api.post('/api/destinations', data),

  update: (id: number, data: DestinationFormData): Promise<AxiosResponse<Destination>> =>
    api.put(`/api/destinations/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/destinations/${id}`),
}

// ─── Usuarios del sistema ─────────────────────────────────────────────────────
export const usersApi = {
  list: (): Promise<AxiosResponse<SystemUser[]>> =>
    api.get('/api/users'),

  create: (data: UserFormData): Promise<AxiosResponse<SystemUser>> =>
    api.post('/api/users', data),

  update: (id: number, data: UserFormData): Promise<AxiosResponse<SystemUser>> =>
    api.put(`/api/users/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/api/users/${id}`),
}

// ─── Dashboard ───────────────────────────────────────────────────────────────
export const dashboardApi = {
  summary: (params?: DashboardFilters): Promise<AxiosResponse<DashboardSummary>> =>
    api.get('/api/dashboard/summary', { params }),

  byClient: (params?: DashboardFilters): Promise<AxiosResponse<KilosByClient[]>> =>
    api.get('/api/dashboard/by-client', { params }),

  bySubcategory: (params?: DashboardFilters): Promise<AxiosResponse<KilosBySubcategory[]>> =>
    api.get('/api/dashboard/by-subcategory', { params }),

  byDestination: (params?: DashboardFilters): Promise<AxiosResponse<KilosByDestination[]>> =>
    api.get('/api/dashboard/by-destination', { params }),

  monthlyTrend: (params?: DashboardFilters): Promise<AxiosResponse<MonthlyTrendPoint[]>> =>
    api.get('/api/dashboard/monthly-trend', { params }),

  operationalAlerts: (params?: DashboardFilters): Promise<AxiosResponse<OperationalAlerts>> =>
    api.get('/api/dashboard/operational-alerts', { params }),
}

// ─── Entregas a disposición final ────────────────────────────────────────────
function buildDeliveryFormData(data: DeliveryFormData, document?: File | null): FormData {
  const form = new FormData()
  form.append('destination_id', String(data.destination_id))
  form.append('delivered_at', data.delivered_at)
  if (data.notes) form.append('notes', data.notes)
  data.items.forEach((item, i) => {
    form.append(`items[${i}][pickup_record_item_id]`, String(item.pickup_record_item_id))
    form.append(`items[${i}][kilos]`, String(item.kilos))
  })
  if (document) form.append('document', document)
  return form
}

export const deliveriesApi = {
  list: (params?: { date_from?: string; date_to?: string }): Promise<AxiosResponse<Delivery[]>> =>
    api.get('/api/deliveries', { params }),

  get: (id: number): Promise<AxiosResponse<Delivery>> =>
    api.get(`/api/deliveries/${id}`),

  create: (data: DeliveryFormData, document?: File | null): Promise<AxiosResponse<Delivery>> => {
    if (document) {
      return api.post('/api/deliveries', buildDeliveryFormData(data, document), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }
    return api.post('/api/deliveries', data)
  },

  update: (id: number, data: DeliveryFormData, document?: File | null): Promise<AxiosResponse<Delivery>> => {
    if (document) {
      const form = buildDeliveryFormData(data, document)
      form.append('_method', 'PUT')
      return api.post(`/api/deliveries/${id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }
    return api.put(`/api/deliveries/${id}`, data)
  },

  getStock: (params?: { exclude_delivery_id?: number }): Promise<AxiosResponse<DeliveryStockItem[]>> =>
    api.get('/api/deliveries/stock', { params }),
}

// ─── Informes ESG ────────────────────────────────────────────────────────────
export const reportsApi = {
  preview: (params: {
    client_id: number
    date_from: string
    date_to: string
  }): Promise<AxiosResponse<EsgReportMetrics>> =>
    api.get('/api/reports/esg/preview', { params }),

  generate: (data: EsgReportFormData): Promise<AxiosResponse<Blob>> =>
    api.post('/api/reports/esg', data, { responseType: 'blob' }),

  list: (params?: { client_id?: number }): Promise<AxiosResponse<EsgReport[]>> =>
    api.get('/api/reports', { params }),

  download: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/api/reports/${id}/download`, { responseType: 'blob' }),
}

// ─── Kilometraje diario del operario ─────────────────────────────────────────
export const operatorDailyLogsApi = {
  getByDate: (date: string): Promise<AxiosResponse<OperatorDailyLog | null>> =>
    api.get('/api/operator-daily-logs', { params: { date } }),

  summary: (params: { date: string; month: string }): Promise<AxiosResponse<OperatorDailyLogSummary>> =>
    api.get('/api/operator-daily-logs/summary', { params }),

  upsert: (data: OperatorDailyLogFormData): Promise<AxiosResponse<OperatorDailyLog>> =>
    api.post('/api/operator-daily-logs', data),
}

export default api
